import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  SimpleGrid,
  Skeleton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { z } from 'zod';
import DefaultPage from '../../../components/shared/DefaultPage';
import InputNumber from '../../../components/shared/Inputs/InputNumber';
import InputSelector from '../../../components/shared/Inputs/InputSelector';
import InputText from '../../../components/shared/Inputs/InputText';
import { useAuth } from '../../../hooks/auth';
import backend from '../../../services/backend';
import { usePermissoes } from '../../../services/hooks/usePermissoes';
import { usePermissoesUsuario } from '../../../services/hooks/usePermissoesUsuario';
import {
  IUsuariosReponse,
  useUsuario,
} from '../../../services/hooks/useUsuario';
import { queryClient } from '../../../services/queryClient';
import { Constants } from '../../../shared/constants';
import AceitaUsuarioModal from '../AceitaUsuarioModal';

const dadosSchema = z.object({
  nome: z.string().nonempty('Nome obrigatório'),
  email: z.string().nonempty('Email obrigatório'),
  telefone: z.string().nonempty('Telefone obrigatório'),
  cpf: z.string().optional(),
  cnpj: z.string().nullable().optional(),
  status: z.enum(['1', '2'], {
    required_error: 'Status obrigatório',
    invalid_type_error: 'Status inválido',
  }),
  comissao_certificado_pai: z
    .number({ required_error: 'Obrigatório' })
    .min(0, 'A'),
  custo_certificado: z.string({ required_error: 'Obrigatório' }),
  comissao_certificado: z.number({ required_error: 'Obrigatório' }).min(0, 'A'),
});

const enderecoSchema = z.object({
  cep: z.string().nonempty('CEP obrigatório'),
  logradouro: z.string().nonempty('Logradouro obrigatório'),
  numero: z.string().nonempty('Numero obrigatório'),
  bairro: z.string().nonempty('Bairro obrigatório'),
  uf: z.string().nonempty('UF obrigatório'),
  cidade: z.string().nonempty('Cidade obrigatório'),
  complemento: z.string().nullable().optional(),
});

const formSchema = z.object({
  usuario: dadosSchema,
  endereco: enderecoSchema,
});

type formType = z.infer<typeof formSchema>;

const EditarUsuario = () => {
  // estilos
  const color = useColorModeValue('#1A202C', 'white');
  const bg = useColorModeValue('white', 'gray.800');
  const bgGreen = useColorModeValue('rgb(0,250,0)', 'rgb(150,250,150)');

  // estados
  const [salvando, setSalvando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [showAceite, setShowAceite] = useState(false);

  // constantes
  const toast = useToast();
  const history = useHistory();
  const { id } = useParams();

  // dados
  const { permissoes: permissoesList } = useAuth();
  const { data: permissoes, isFetching: isPermissoesFetching } =
    usePermissoesUsuario(id);
  const [permissoesData, setPermissoesData] = useState([]);
  const { data, isLoading } = useUsuario(id);
  const usuarioData: IUsuariosReponse | null = data ?? null;
  const { permite } = usePermissoes();
  const {
    PERMISSAO_CONVIDAR_PARCEIROS,
    PERMISSAO_EDITAR_PERMISSOES,
    PERMISSAO_EDITAR_COMISSAO,
  } = Constants;

  useEffect(() => {
    if (permissoes) {
      setPermissoesData(permissoes);
    }
  }, [permissoes]);

  const formMethods = useForm<formType>({
    resolver: zodResolver(formSchema),
  });

  const {
    handleSubmit,
    reset,
    watch,
    register,
    setValue,
    control,
    formState: { errors },
  } = formMethods;

  const valorCEP = watch('endereco.cep');
  const statusUsuario = watch('usuario.status');
  const comissao_certificado = watch('usuario.comissao_certificado');
  const custo_certificado = watch('usuario.custo_certificado');
  const comissao_certificado_pai = watch('usuario.comissao_certificado_pai');

  const custoCertificado = Number(
    Number(comissao_certificado_pai ?? 0) +
      Number(usuarioData?.usuario.usuario_pai.custo_certificado ?? 0),
  );

  const valorCertificado = (
    Number(comissao_certificado ?? 0) + Number(custoCertificado ?? 0)
  ).toFixed(2);

  useEffect(() => {
    if (!usuarioData) return;
    reset({
      usuario: {
        nome: usuarioData.usuario?.nome ?? '',
        cpf: usuarioData.usuario?.cpf ?? null,
        cnpj: usuarioData.usuario?.cnpj ?? null,
        email: usuarioData.usuario?.email ?? '',
        telefone: usuarioData.usuario?.telefone ?? null,
        status: usuarioData.usuario?.status ?? '',
        comissao_certificado_pai: Number(
          usuarioData.usuario.usuario_pai.comissao_certificado ?? 0,
        ),
        custo_certificado: Number(
          (Number(usuarioData?.usuario?.usuario_pai?.comissao_certificado) ??
            0) +
            (Number(usuarioData?.usuario?.usuario_pai?.custo_certificado) ?? 0),
        ).toFixed(2),
        comissao_certificado:
          Number(usuarioData?.usuario?.comissao_certificado) ?? 0,
      },
      endereco: {
        cep: usuarioData.endereco?.cep ?? '',
        uf: usuarioData.endereco?.uf ?? '',
        cidade: usuarioData.endereco?.cidade ?? '',
        bairro: usuarioData.endereco?.bairro ?? '',
        numero: usuarioData.endereco?.numero ?? '',
        logradouro: usuarioData.endereco?.logradouro ?? '',
        complemento: usuarioData.endereco?.complemento ?? null,
      },
    });
  }, [usuarioData, reset]);

  useEffect(() => {
    setValue('usuario.custo_certificado', String(custoCertificado));
  }, [custoCertificado]);

  const buscaCep = async () => {
    if (buscandoCep) return;
    if (valorCEP?.length < 9 || valorCEP === '') {
      toast({
        title: `Insira um CEP válido`,
        status: 'info',
        duration: 9000,
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }
    setBuscandoCep(true);
    try {
      const response = await backend.get('/cep', {
        params: { cep: valorCEP },
      });
      const dados = response.data;
      setValue('endereco.logradouro', dados?.logradouro);
      setValue('endereco.complemento', dados?.complemento);
      setValue('endereco.bairro', dados?.bairro);
      setValue('endereco.cidade', dados?.localidade);
      setValue('endereco.uf', dados?.uf);
    } catch (error) {
      toast({
        title: error?.response?.data?.message || 'Erro ao buscar CEP',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setBuscandoCep(false);
    }
  };

  const SalvaUsuario = async (data: formType) => {
    console.log(data);
    if (salvando) return;
    setSalvando(true);
    try {
      const response = await backend.post(`/usuario/${id}`, {
        usuario: data.usuario,
        endereco: data.endereco,
        permissoes: permissoesData,
      });
      await queryClient.invalidateQueries({
        queryKey: ['usuarios'],
        // refetchType: 'all',
      });
      history.push('/usuarios');
    } catch (error) {
      //
    } finally {
      setSalvando(false);
    }
  };

  return (
    <DefaultPage>
      {showAceite && (
        <AceitaUsuarioModal
          open={showAceite}
          close={() => setShowAceite(false)}
          id_usuario={id}
        />
      )}
      <Flex mt="12" align="center">
        <IconButton
          aria-label="voltar"
          variant="ghost"
          icon={<ArrowLeft />}
          bg="none"
          color={color}
          _focus={{ outline: 'none', boxShadow: 'none' }}
          onClick={() => {
            if (permite(PERMISSAO_CONVIDAR_PARCEIROS)) {
              history.push('/usuarios');
            } else {
              history.push('/dashboard');
            }
          }}
          mr="2"
        />
        <Heading fontSize="2xl">Editar Usuário</Heading>
      </Flex>
      <Flex>
        {isLoading ? (
          <Box
            bg={bg}
            rounded="2xl"
            borderWidth="1px"
            p="4"
            w="100%"
            my="4"
            justify="center"
          >
            <Flex justify="center">
              <Spinner color="blue.500" />
            </Flex>
          </Box>
        ) : (
          <Box
            bg={bg}
            rounded="2xl"
            borderWidth="1px"
            p="4"
            w="100%"
            my="4"
            //
          >
            <form
              onSubmit={handleSubmit(SalvaUsuario, errors => {
                console.log(errors);
              })}
            >
              <Tabs>
                <TabList>
                  <Tab color={color}>Dados</Tab>
                  <Tab color={color}>Endereço</Tab>
                  <Tab color={color}>Permissões</Tab>
                  <Tab color={color}>Comissão</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <SimpleGrid
                      gap="2"
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                    >
                      <Controller
                        control={control}
                        name="usuario.nome"
                        render={({ field }) => (
                          <InputText
                            label="Nome"
                            labelColor={color}
                            {...field}
                            error={errors.usuario?.nome}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="usuario.cpf"
                        render={({ field }) => (
                          <InputText
                            label="CPF"
                            labelColor={color}
                            {...field}
                            error={errors.usuario?.cpf}
                            mask="000.000.000-00"
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="usuario.cnpj"
                        render={({ field }) => (
                          <InputText
                            label="CNPJ"
                            labelColor={color}
                            {...field}
                            error={errors.usuario?.cnpj}
                            mask="00.000.000/0000-00"
                          />
                        )}
                      />
                    </SimpleGrid>
                    <SimpleGrid
                      gap="2"
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                    >
                      <Controller
                        control={control}
                        name="usuario.status"
                        render={({ field }) => (
                          <Flex align="flex-end">
                            <InputSelector
                              {...field}
                              label="Status"
                              labelColor={color}
                              disableSelecione
                              disabled={statusUsuario === '3'}
                              // borderRadius="0px"
                              borderTopRightRadius="0px"
                              borderBottomRightRadius="0px"
                              options={
                                statusUsuario === '3'
                                  ? [
                                      {
                                        label: 'Aguardando Confirmação',
                                        value: '3',
                                      },
                                    ]
                                  : [
                                      {
                                        label: 'Ativo',
                                        value: '1',
                                      },
                                      {
                                        label: 'Inativo',
                                        value: '2',
                                      },
                                    ]
                              }
                            />
                            {statusUsuario === '3' && (
                              <Button
                                rounded="0px"
                                variant="ghost"
                                colorScheme="blue"
                                borderWidth="1px"
                                onClick={() => setShowAceite(true)}
                              >
                                Aceitar usuário
                              </Button>
                            )}
                          </Flex>
                        )}
                      />
                      <Controller
                        control={control}
                        name="usuario.email"
                        render={({ field }) => (
                          <InputText
                            isDisabled
                            type="email"
                            label="Email"
                            labelColor={color}
                            {...field}
                            error={errors.usuario?.email}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="usuario.telefone"
                        render={({ field }) => (
                          <InputText
                            label="Telefone"
                            labelColor={color}
                            {...field}
                            error={errors.usuario?.telefone}
                            mask="(00) 00000-0000"
                          />
                        )}
                      />
                    </SimpleGrid>
                  </TabPanel>
                  <TabPanel>
                    <SimpleGrid
                      gap="2"
                      templateColumns={['1fr', '1fr', '3fr 1fr 3fr']}
                    >
                      <Controller
                        control={control}
                        name="endereco.cep"
                        render={({ field }) => (
                          <InputText
                            label="CEP"
                            labelColor={color}
                            {...field}
                            error={errors.endereco?.cep}
                            mask="00000-000"
                            inputRightAddon={
                              <InputRightAddon
                                cursor="pointer"
                                onClick={buscaCep}
                              >
                                {!buscandoCep ? (
                                  <Search color={color} />
                                ) : (
                                  <Spinner />
                                )}
                              </InputRightAddon>
                            }
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="endereco.uf"
                        render={({ field }) => (
                          <InputText
                            label="UF"
                            labelColor={color}
                            {...field}
                            error={errors.endereco?.uf}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="endereco.cidade"
                        render={({ field }) => (
                          <InputText
                            label="Cidade"
                            labelColor={color}
                            {...field}
                            error={errors.endereco?.cidade}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="endereco.bairro"
                        render={({ field }) => (
                          <InputText
                            label="Bairro"
                            labelColor={color}
                            {...field}
                            error={errors.endereco?.bairro}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="endereco.numero"
                        render={({ field }) => (
                          <InputText
                            label="Número"
                            labelColor={color}
                            {...field}
                            error={errors.endereco?.numero}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="endereco.logradouro"
                        render={({ field }) => (
                          <InputText
                            label="Logradouro"
                            labelColor={color}
                            {...field}
                            error={errors.endereco?.logradouro}
                          />
                        )}
                      />
                    </SimpleGrid>
                    <Controller
                      control={control}
                      name="endereco.complemento"
                      render={({ field }) => (
                        <InputText
                          label="Complemento"
                          labelColor={color}
                          {...field}
                          error={errors.endereco?.complemento}
                        />
                      )}
                    />
                  </TabPanel>
                  <TabPanel>
                    <Flex
                      h="400px"
                      maxH="400px"
                      overflowY="auto"
                      flexDir="column"
                      w="50%"
                    >
                      {isPermissoesFetching && (
                        <>
                          <Skeleton w="100%" h="35px" mt="2" />
                          <Skeleton w="100%" h="35px" mt="2" />
                          <Skeleton w="100%" h="35px" mt="2" />
                        </>
                      )}
                      {permissoesData?.map(item => {
                        return (
                          <Flex justify="space-between" borderWidth="1px" p="2">
                            <Text color={color}>{item?.nome}</Text>
                            <Checkbox
                              isDisabled={!permite(PERMISSAO_EDITAR_PERMISSOES)}
                              isChecked={item?.marcada}
                              onChange={e => {
                                setPermissoesData(prev =>
                                  prev.map(p =>
                                    p.id === item.id
                                      ? { ...p, marcada: e.target.checked }
                                      : p,
                                  ),
                                );
                              }}
                            />
                          </Flex>
                        );
                      })}
                    </Flex>
                  </TabPanel>
                  <TabPanel>
                    <Text color={color}>
                      Parceiro pai: {usuarioData?.usuario?.usuario_pai?.nome}
                    </Text>
                    <SimpleGrid
                      mt="2"
                      w={['100%', '100%', '50%']}
                      templateColumns={['1fr 1fr', '1fr 3fr', '1fr, 6fr']}
                    >
                      <InputGroup>
                        <InputLeftAddon
                          fontWeight="medium"
                          color={color}
                          h="100%"
                          py="2"
                          roundedBottom="0px"
                        >
                          R$
                        </InputLeftAddon>
                        <Flex
                          w="100%"
                          align="center"
                          justify="space-between"
                          padding="0px"
                          borderWidth="1px"
                          borderTopRightRadius="xl"
                          bg="rgba(0,0,0,0.09)"
                        >
                          <Text color={color} pl="3" alignSelf="center">
                            {data?.usuario?.usuario_pai?.custo_certificado}
                          </Text>
                        </Flex>
                      </InputGroup>
                      <Text color={color} pl="2" alignSelf="center">
                        Custo do parceiro pai
                      </Text>
                      <Controller
                        control={control}
                        name="usuario.comissao_certificado_pai"
                        render={({ field }) => {
                          return (
                            <InputGroup>
                              <InputLeftAddon
                                fontWeight="medium"
                                color={color}
                                rounded="0px"
                                borderBottomLeftRadius="xl"
                              >
                                R$
                              </InputLeftAddon>
                              <InputNumber
                                isDisabled={!permite(PERMISSAO_EDITAR_COMISSAO)}
                                color={color}
                                maskType="money"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                error={errors.usuario?.comissao_certificado_pai}
                                borderRadius="0px"
                                borderBottomRightRadius="xl"
                              />
                            </InputGroup>
                          );
                        }}
                      />
                      <Text color={color} pl="2" alignSelf="center">
                        Comissão do parceiro pai
                      </Text>
                    </SimpleGrid>
                    <SimpleGrid
                      mt="2"
                      w={['100%', '100%', '50%']}
                      templateColumns={['1fr 1fr', '1fr 3fr', '1fr, 6fr']}
                    >
                      <InputGroup>
                        <InputLeftAddon
                          fontWeight="medium"
                          color={color}
                          h="100%"
                          roundedBottom="0px"
                        >
                          R$
                        </InputLeftAddon>
                        <Flex
                          w="100%"
                          align="center"
                          padding="7px"
                          borderWidth="1px"
                          borderTopRightRadius="xl"
                          bg="rgba(0,0,0,0.05)"
                        >
                          <Text color={color} pl="2">
                            {custoCertificado.toFixed(2)}
                          </Text>
                        </Flex>
                      </InputGroup>
                      <Text color={color} pl="2" alignSelf="center">
                        Custo certificado
                      </Text>

                      <Controller
                        control={control}
                        name="usuario.comissao_certificado"
                        render={({ field }) => {
                          return (
                            <InputGroup>
                              <InputLeftAddon
                                fontWeight="medium"
                                color={color}
                                rounded="0px"
                              >
                                R$
                              </InputLeftAddon>
                              <InputNumber
                                isDisabled={!permite(PERMISSAO_EDITAR_COMISSAO)}
                                color={color}
                                maskType="money"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                error={errors.usuario?.comissao_certificado}
                                borderRadius="0px"
                              />
                            </InputGroup>
                          );
                        }}
                      />
                      <Text color={color} pl="2" alignSelf="center">
                        Comissão sugerida
                      </Text>

                      <InputGroup>
                        <InputLeftAddon roundedTop="0px">
                          <Text
                            bgGradient="linear(to-b, green.300, green.500)"
                            bgClip="text"
                            fontWeight="bold"
                          >
                            R$
                          </Text>
                        </InputLeftAddon>
                        <Flex
                          align="center"
                          padding="7px"
                          borderWidth="1px"
                          w="100%"
                          borderBottomRightRadius="xl"
                          bg="rgba(0,0,0,0.05)"
                        >
                          <Text
                            fontWeight="medium"
                            bgGradient="linear(to-b, green.300, green.500)"
                            bgClip="text"
                            pl="2"
                          >
                            {valorCertificado}
                          </Text>
                        </Flex>
                      </InputGroup>
                      <Text color={color} pl="2" alignSelf="center">
                        Valor de venda do certificado
                      </Text>
                    </SimpleGrid>
                  </TabPanel>
                </TabPanels>
              </Tabs>
              {statusUsuario !== '3' && (
                <Flex justify="flex-end">
                  <Button
                    mr="2"
                    variant="outline"
                    onClick={() => history.push('/usuarios')}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {salvando ? (
                      <>
                        <Spinner /> Salvando...
                      </>
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                </Flex>
              )}
            </form>
          </Box>
        )}
      </Flex>
    </DefaultPage>
  );
};

export default EditarUsuario;
